import React, { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Alert from "@cloudscape-design/components/alert";
import Spinner from "@cloudscape-design/components/spinner";
import Badge from "@cloudscape-design/components/badge";
import Container from "@cloudscape-design/components/container";

import { defaultPrizeTypeOrder, prizeSettingsModalStore } from "../stores/prizeSettingsModal";

const PRIZE_TYPE_LABELS = {
	item: "Item",
	chest: "Chest",
	freespins: "Freespins",
	tickets: "Tickets",
	money: "Money",
};

const normalizeOrder = (order) => {
	const values = Array.isArray(order) ? order : [];
	const result = [];

	values.forEach((type) => {
		if (defaultPrizeTypeOrder.includes(type) && !result.includes(type)) {
			result.push(type);
		}
	});

	defaultPrizeTypeOrder.forEach((type) => {
		if (!result.includes(type)) {
			result.push(type);
		}
	});

	return result;
};

const PrizeModalSettings = ({ Interface }) => {
	const prizeSettingsModal = useRecoilValue(prizeSettingsModalStore);
	const setPrizeSettingsModal = useSetRecoilState(prizeSettingsModalStore);
	const [dragType, setDragType] = useState(null);

	useEffect(() => {
		if (!prizeSettingsModal.show) return;

		setPrizeSettingsModal((prev) => ({
			...prev,
			loaded: false,
			error: "",
		}));

		Interface.prizeGetTypeOrder()
			.then((order) => {
				setPrizeSettingsModal((prev) => ({
					...prev,
					loaded: true,
					error: "",
					order: normalizeOrder(order),
				}));
			})
			.catch((error) => {
				setPrizeSettingsModal((prev) => ({
					...prev,
					loaded: true,
					error: error?.text || error?.message || "Failed to load prize settings",
					order: normalizeOrder(prev.order),
				}));
			});
	}, [Interface, prizeSettingsModal.show, setPrizeSettingsModal]);

	const close = () => {
		setPrizeSettingsModal((prev) => ({
			...prev,
			show: false,
			error: "",
		}));
	};

	const moveType = (sourceType, targetType) => {
		if (!sourceType || !targetType || sourceType === targetType) return;

		setPrizeSettingsModal((prev) => {
			const order = normalizeOrder(prev.order);
			const sourceIndex = order.indexOf(sourceType);
			const targetIndex = order.indexOf(targetType);

			if (sourceIndex === -1 || targetIndex === -1) {
				return prev;
			}

			const nextOrder = [...order];
			const [moved] = nextOrder.splice(sourceIndex, 1);
			nextOrder.splice(targetIndex, 0, moved);

			return {
				...prev,
				order: nextOrder,
			};
		});
	};

	const save = () => {
		const order = normalizeOrder(prizeSettingsModal.order);

		setPrizeSettingsModal((prev) => ({
			...prev,
			loaded: false,
			error: "",
		}));

		Interface.prizeUpdateTypeOrder(order)
			.then((updatedOrder) => {
				setPrizeSettingsModal((prev) => ({
					...prev,
					loaded: true,
					show: false,
					error: "",
					order: normalizeOrder(updatedOrder),
				}));
				Interface.addNoty("Success", "Prize settings saved");
			})
			.catch((error) => {
				setPrizeSettingsModal((prev) => ({
					...prev,
					loaded: true,
					error: error?.text || error?.message || "Failed to save prize settings",
				}));
			});
	};

	const order = normalizeOrder(prizeSettingsModal.order);

	return (
		<Modal
			visible={prizeSettingsModal.show}
			header="Prize Settings"
			onDismiss={close}
			size="medium"
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button variant="link" onClick={close}>
							Cancel
						</Button>
						<Button variant="primary" loading={!prizeSettingsModal.loaded} onClick={save}>
							Save settings
						</Button>
					</SpaceBetween>
				</Box>
			}>
			<SpaceBetween direction="vertical" size="m">
				{prizeSettingsModal.error && <Alert type="error">{prizeSettingsModal.error}</Alert>}

				{!prizeSettingsModal.loaded ? (
					<Box textAlign="center" padding="xl">
						<Spinner size="large" />
					</Box>
				) : (
					<SpaceBetween direction="vertical" size="m">
						{order.map((type, index) => (
							<div
								key={type}
								draggable
								onDragStart={(event) => {
									event.dataTransfer.setData("text/plain", type);
									setDragType(type);
								}}
								onDragEnd={() => setDragType(null)}
								onDragOver={(event) => event.preventDefault()}
								onDrop={(event) => {
									event.preventDefault();
									moveType(event.dataTransfer.getData("text/plain"), type);
									setDragType(null);
								}}>
								<Container>
									<SpaceBetween direction="horizontal" size="s" alignItems="center">
										<Box color="text-body-secondary">:::</Box>
										<Badge color={dragType === type ? "blue" : "grey"}>{index + 1}</Badge>
										<Box fontWeight="bold">{PRIZE_TYPE_LABELS[type] || type}</Box>
										<Box color="text-body-secondary">({type})</Box>
									</SpaceBetween>
								</Container>
							</div>
						))}
					</SpaceBetween>
				)}
			</SpaceBetween>
		</Modal>
	);
};

export default PrizeModalSettings;
